import { useId, type ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { Combobox, type ComboboxProps } from "@/shared/components/ui/Combobox";
import { Label } from "@/shared/components/ui/Label";

/**
 * Form-field wrapper for the searchable Combobox (design.md §10), mirroring
 * SelectFormField's label/error/hint layout and mx-4 alignment.
 */
export interface ComboboxFormFieldProps extends ComboboxProps {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
}

export function ComboboxFormField({
  label,
  hint,
  error,
  id,
  className,
  ...comboboxProps
}: ComboboxFormFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint || error ? `${inputId}-hint` : undefined;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label htmlFor={inputId} className="mx-4">
        {label}
      </Label>
      <Combobox
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={hintId}
        {...comboboxProps}
      />
      {error ? (
        <p id={hintId} className="mx-4 type-body-sm text-danger-strong">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mx-4 type-body-sm text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
